import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useOrder } from '@/contexts/OrderContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const { cart, getCartTotal } = useCart();
  const { orderType, calculatePackagingCharges, setCustomerDetails } = useOrder();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    instructions: ''
  });

  const subtotal = getCartTotal();
  const packagingCharges = calculatePackagingCharges(cart);
  const total = subtotal + packagingCharges;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (orderType === 'delivery' && !formData.address) {
      toast.error('Delivery address is required');
      return;
    }
    setCustomerDetails(formData);
    navigate('/payment');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <header className="bg-gray-900/80 backdrop-blur-md border-b border-amber-500/30">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button onClick={() => navigate('/cart')} variant="ghost" className="text-amber-400 hover:bg-amber-500/10">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold text-amber-400 font-['Playfair_Display']">Checkout</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6 bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30">
            <h2 className="text-xl font-bold text-amber-400 mb-6">Customer Details</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-amber-200">Full Name *</Label>
                <Input name="name" value={formData.name} onChange={handleChange} required className="mt-1 bg-gray-700 border-amber-500/30 text-white" />
              </div>
              <div>
                <Label className="text-amber-200">Phone Number *</Label>
                <Input name="phone" type="tel" value={formData.phone} onChange={handleChange} required className="mt-1 bg-gray-700 border-amber-500/30 text-white" />
              </div>
              {orderType === 'delivery' && (
                <div>
                  <Label className="text-amber-200">Delivery Address *</Label>
                  <Textarea name="address" value={formData.address} onChange={handleChange} required className="mt-1 bg-gray-700 border-amber-500/30 text-white" rows={3} />
                </div>
              )}
              <div>
                <Label className="text-amber-200">Special Instructions</Label>
                <Textarea name="instructions" value={formData.instructions} onChange={handleChange} className="mt-1 bg-gray-700 border-amber-500/30 text-white" rows={2} />
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-red-600 font-bold py-6 text-lg">
                Continue to Payment
              </Button>
            </form>
          </Card>

          <div className="space-y-4">
            <Card className="p-6 bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30">
              <h2 className="text-xl font-bold text-amber-400 mb-4">Order Summary</h2>
              <div className="space-y-3 mb-4">
                {cart.map(item => (
                  <div key={item.id} className="flex justify-between text-sm text-amber-100">
                    <span>{item.name} x {item.quantity}</span>
                    <span>₹{item.price * item.quantity}</span>
                  </div>
                ))}
              </div>
              <div className="border-t-2 border-amber-500/30 pt-4 space-y-2">
                <div className="flex justify-between text-amber-200">
                  <span>Subtotal</span>
                  <span>₹{subtotal}</span>
                </div>
                <div className="flex justify-between text-amber-200">
                  <span>Packaging</span>
                  <span>₹{packagingCharges}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-amber-400">
                  <span>Total</span>
                  <span>₹{total}</span>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-green-500 to-teal-600 text-white">
              <p className="font-semibold">Order Type: {orderType.toUpperCase()}</p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
